import { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Turnstile } from "@marsidev/react-turnstile";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Clock,
  MessageSquare,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
  Check,
  Mail,
} from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { absoluteUrl } from "@/lib/site-config";
import Header from "@/components/landing/Header";
import { emailRules, requireNonEmpty } from "@/lib/validators";
import { resolveApiError } from "@/lib/api-errors";

const API_URL = process.env.REACT_APP_API_URL || "";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const FIELD_CLASS =
  "mt-2 w-full rounded-xl border border-violet-light bg-white px-4 py-3 text-[15px] text-ink shadow-sm outline-none transition-all duration-150 focus:border-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20 placeholder:text-ink-3";
const LABEL_CLASS = "block text-sm font-semibold text-ink";
const ERROR_CLASS = "mt-1.5 text-[13px] text-red-500";

const ROLES = [
  { value: "pastor", label: "Pastor / Pastora" },
  { value: "lider", label: "Líder de área" },
  { value: "secretario", label: "Secretario/a" },
  { value: "tesorero", label: "Tesorero/a" },
  { value: "otro", label: "Otro" },
];

const SIZES = [
  { value: "1-50", label: "1 a 50 miembros" },
  { value: "51-150", label: "51 a 150 miembros" },
  { value: "151-500", label: "151 a 500 miembros" },
  { value: "+500", label: "Más de 500 miembros" },
];

const TRUST = [
  { Icon: Clock, text: "Respondemos en menos de 24 horas" },
  { Icon: MessageSquare, text: "Sin presión, solo una conversación" },
  { Icon: ShieldCheck, text: "Tus datos están seguros con nosotros" },
];

function CustomSelect({ options, placeholder, value, onChange, onBlur, id }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onBlur]);

  return (
    <div ref={ref} className="relative mt-2">
      <button
        id={id}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-left text-[15px] shadow-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-violet-fuerte/20 ${
          open
            ? "border-violet-fuerte ring-2 ring-violet-fuerte/20"
            : "border-violet-light"
        }`}
      >
        <span className={selected ? "text-ink" : "text-ink-3"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <motion.ul
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl border border-violet-light bg-white py-1 shadow-lg"
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt.value);
                setOpen(false);
              }}
              className={`cursor-pointer px-4 py-2.5 text-[15px] transition-colors duration-100 ${
                opt.value === value
                  ? "bg-violet-light font-semibold text-violet-fuerte"
                  : "text-ink hover:bg-violet-light/50"
              }`}
            >
              {opt.label}
            </li>
          ))}
        </motion.ul>
      )}
    </div>
  );
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function ContactPage() {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [submitState, setSubmitState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const turnstileRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const onSubmit = async (data) => {
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErrorMessage("Completa la verificación de seguridad antes de continuar.");
      return;
    }
    setSubmitState("loading");
    setErrorMessage("");
    try {
      await axios.post(`${API_URL}/public/contact`, {
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        churchName: data.churchName.trim(),
        role: data.role || undefined,
        size: data.size || undefined,
        message: data.message.trim(),
        turnstileToken: turnstileToken || null,
      });
      setSubmitState("success");
      reset();
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } catch (err) {
      setErrorMessage(resolveApiError(err));
      setSubmitState("error");
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  return (
    <div className="flex min-h-screen w-full overflow-hidden">
      <Seo
        title="Contacto"
        description="Habla con el equipo de Ekklesia. Resolvemos dudas sobre planes, funciones y cómo organizar la iglesia con nuestro software. Respuesta en menos de 24 horas."
        path="/contacto"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contacto | Ekklesia",
          description:
            "Formulario de contacto para iglesias interesadas en el software de gestión Ekklesia.",
          url: absoluteUrl("/contacto"),
        }}
      />
      <Header />

      {/* ── LEFT PANEL — dark ── */}
      <div
        className="relative hidden lg:flex lg:w-[44%] xl:w-[42%] flex-col justify-center px-12 xl:px-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(165deg, #1A1135 0%, #2E2458 52%, #1F1838 100%)",
        }}
      >
        {/* decorative glows */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-1 w-full"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(201,162,39,0.7), transparent)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1/4 -left-20 h-[400px] w-[400px] rounded-full opacity-20 blur-[80px]"
          style={{ background: "radial-gradient(circle, #4A3A8A 0%, transparent 70%)" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-1/4 right-0 h-[300px] w-[300px] rounded-full opacity-15 blur-[70px]"
          style={{ background: "radial-gradient(circle, #C9A227 0%, transparent 70%)" }}
        />

        <div className="relative z-10">
          <motion.p
            {...fadeUp(0.05)}
            className="font-display text-xs font-bold uppercase tracking-[0.22em] text-gold"
          >
            Contacto
          </motion.p>

          <motion.h1
            {...fadeUp(0.12)}
            className="mt-4 font-display text-4xl font-black leading-[1.06] tracking-tight text-white xl:text-5xl"
          >
            Hablemos<br />
            <span className="text-white/50">sin presión.</span>
          </motion.h1>

          <motion.p
            {...fadeUp(0.2)}
            className="mt-5 max-w-xs text-[17px] leading-relaxed text-white/55"
          >
            Cuéntanos qué necesita la iglesia. Estamos aquí para encontrar
            contigo la mejor solución.
          </motion.p>

          <motion.ul
            {...fadeUp(0.28)}
            className="mt-10 space-y-4"
          >
            {TRUST.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.07] text-gold">
                  <Icon className="h-[17px] w-[17px]" strokeWidth={1.75} />
                </span>
                <span className="text-[15px] text-white/65">{text}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div {...fadeUp(0.36)} className="mt-10">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white/30">
              O escríbenos directamente
            </p>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="mailto:hola@ekklesiaonline.app"
                  className="group inline-flex items-center gap-2.5 text-[14px] text-white/55 transition-colors duration-200 hover:text-white"
                >
                  <Mail className="h-4 w-4 shrink-0 text-gold/70 transition-colors duration-200 group-hover:text-gold" strokeWidth={1.75} />
                  hola@ekklesiaonline.app
                </a>
              </li>
            </ul>
          </motion.div>

          <motion.a
            {...fadeUp(0.42)}
            href="/"
            className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white/30 transition-colors duration-200 hover:text-white/70"
          >
            <ArrowRight className="h-4 w-4 rotate-180 transition-transform duration-200 group-hover:-translate-x-0.5" />
            Volver al inicio
          </motion.a>
        </div>
      </div>

      {/* ── RIGHT PANEL — light ── */}
      <div className="flex flex-1 flex-col items-center justify-center bg-white px-4 pb-10 pt-24 sm:px-8 lg:px-12 lg:pt-0 xl:px-16">
        {submitState === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="flex w-full max-w-md flex-col items-center gap-4 rounded-3xl border border-violet-light bg-violet-light/30 px-8 py-14 text-center shadow-sm"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-fuerte/10">
              <CheckCircle2 className="h-8 w-8 text-violet-fuerte" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
              ¡Mensaje recibido!
            </h2>
            <p className="max-w-xs text-base leading-relaxed text-ink-2">
              Gracias por escribirnos. Te responderemos en el correo que nos
              dejaste en menos de 24 horas.
            </p>
            <button
              onClick={() => setSubmitState("idle")}
              className="mt-2 text-sm font-semibold text-violet-fuerte hover:underline"
            >
              Enviar otro mensaje
            </button>
          </motion.div>
        ) : (
          <div className="w-full max-w-lg">
            {/* Mobile-only heading */}
            <div className="mb-8 lg:hidden">
              <p className="font-display text-xs font-bold uppercase tracking-[0.22em] text-gold">
                Contacto
              </p>
              <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-ink">
                Hablemos
              </h1>
            </div>

            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-5"
              onSubmit={handleSubmit(onSubmit)}
              data-testid="contact-form"
              noValidate
            >
              {/* Nombre + Email */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className={LABEL_CLASS}>
                    Nombre
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    autoComplete="name"
                    className={FIELD_CLASS}
                    placeholder="Tu nombre"
                    maxLength={200}
                    {...register("name", {
                      required: "El nombre es obligatorio.",
                      maxLength: { value: 200, message: "El nombre no puede superar 200 caracteres." },
                      validate: requireNonEmpty("El nombre no puede estar vacío."),
                    })}
                  />
                  {errors.name && <p className={ERROR_CLASS}>{errors.name.message}</p>}
                </div>
                <div>
                  <label htmlFor="contact-email" className={LABEL_CLASS}>
                    Correo electrónico
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    autoComplete="email"
                    className={FIELD_CLASS}
                    placeholder="nombre@iglesia.org"
                    maxLength={200}
                    {...register("email", emailRules())}
                  />
                  {errors.email && <p className={ERROR_CLASS}>{errors.email.message}</p>}
                </div>
              </div>

              {/* Iglesia */}
              <div>
                <label htmlFor="contact-church" className={LABEL_CLASS}>
                  Nombre de la iglesia
                </label>
                <input
                  id="contact-church"
                  type="text"
                  autoComplete="organization"
                  className={FIELD_CLASS}
                  placeholder="Iglesia Ejemplo"
                  {...register("churchName", {
                    required: "El nombre de la iglesia es obligatorio.",
                    validate: requireNonEmpty("El nombre no puede estar vacío."),
                  })}
                />
                {errors.churchName && (
                  <p className={ERROR_CLASS}>{errors.churchName.message}</p>
                )}
              </div>

              {/* Rol + Tamaño */}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-role" className={LABEL_CLASS}>
                    Tu rol{" "}
                    <span className="font-normal text-ink-3">(opcional)</span>
                  </label>
                  <Controller
                    name="role"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomSelect
                        id="contact-role"
                        options={ROLES}
                        placeholder="Selecciona uno"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                </div>
                <div>
                  <label htmlFor="contact-size" className={LABEL_CLASS}>
                    Tamaño{" "}
                    <span className="font-normal text-ink-3">(opcional)</span>
                  </label>
                  <Controller
                    name="size"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <CustomSelect
                        id="contact-size"
                        options={SIZES}
                        placeholder="Selecciona uno"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Mensaje */}
              <div>
                <label htmlFor="contact-message" className={LABEL_CLASS}>
                  Mensaje
                </label>
                <textarea
                  id="contact-message"
                  rows={4}
                  className={`${FIELD_CLASS} resize-none`}
                  placeholder="¿En qué podemos ayudarte?"
                  maxLength={5000}
                  {...register("message", {
                    required: "El mensaje es obligatorio.",
                    maxLength: { value: 5000, message: "El mensaje no puede superar 5000 caracteres." },
                    validate: requireNonEmpty("El mensaje no puede estar vacío."),
                  })}
                />
                {errors.message && (
                  <p className={ERROR_CLASS}>{errors.message.message}</p>
                )}
              </div>

              {/* Turnstile */}
              {TURNSTILE_SITE_KEY && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                  options={{ theme: "light", language: "es" }}
                />
              )}

              {/* Política de privacidad */}
              <div>
                <label className="flex cursor-pointer items-start gap-3">
                  <div className="relative mt-0.5 flex shrink-0">
                    <input
                      type="checkbox"
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-violet-light bg-white shadow-sm outline-none transition-all duration-150 checked:border-violet-fuerte checked:bg-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20"
                      {...register("privacy", {
                        required: "Debes aceptar la política de privacidad para continuar.",
                      })}
                    />
                    <Check
                      className="pointer-events-none absolute inset-0 m-auto hidden h-3 w-3 text-white peer-checked:block"
                      strokeWidth={3}
                    />
                  </div>
                  <span className="text-[13px] leading-snug text-ink-2">
                    He leído y acepto la{" "}
                    <a
                      href="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-violet-fuerte underline-offset-2 hover:underline"
                    >
                      política de privacidad
                    </a>
                    . Consiento el tratamiento de mis datos para responder a mi
                    consulta.
                  </span>
                </label>
                {errors.privacy && (
                  <p className={ERROR_CLASS}>{errors.privacy.message}</p>
                )}
              </div>

              {/* Error */}
              {submitState === "error" && errorMessage && (
                <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-600">
                  {errorMessage}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitState === "loading"}
                className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[16px] font-semibold text-white shadow-[0_4px_20px_-4px_rgba(74,58,138,0.45)] transition-all duration-200 hover:bg-violet-oscuro hover:shadow-[0_6px_28px_-4px_rgba(74,58,138,0.55)] hover:-translate-y-px disabled:opacity-60 disabled:translate-y-0"
              >
                {submitState === "loading" ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  "Enviar mensaje"
                )}
              </button>

              <p className="text-center text-[13px] text-ink-3">
                Sin compromiso &middot; Respondemos en menos de 24 h
              </p>
            </motion.form>
          </div>
        )}
      </div>
    </div>
  );
}
